import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fetchCached, resolveImageUrl } from '@/lib/api';
import SectionWatermark from '@/components/SectionWatermark';
import {
  Heart,
  Users,
  ShieldCheck,
  Gem,
  Eye,
  Handshake,
  HandHelping,
  ArrowRight,
  CalendarDays,
  Sparkles,
} from 'lucide-react';

const ICON_MAP = {
  heart: Heart,
  'hand-helping': HandHelping,
  users: Users,
  'shield-check': ShieldCheck,
  gem: Gem,
  eye: Eye,
  handshake: Handshake,
};

const FALLBACK_HOME_CONTENT = {
  hero_title: 'MILLION STARS COMMUNITY MINISTRY',
  hero_subtitle: 'Shining the light of Christ among the nations...',
  hero_cta_text: 'Join Us',
  hero_cta_link: '/contact',
  hero_bg_image: 'https://images.unsplash.com/photo-1761258779622-454cbecb2006?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjaHVyY2glMjB3b3JzaGlwJTIwY29uZ3JlZ2F0aW9ufGVufDB8fHx8MTc3NDU0NTE5NHww&ixlib=rb-4.1.0&q=85',
  welcome_title: 'Welcome to Our Ministry',
  welcome_text: "Million Stars Community Ministry is a faith-based community dedicated to spreading the love of Christ across nations and cultures. We believe that every person is a star in God's Kingdom, called to shine brightly and bring hope, healing, and transformation to communities worldwide.",
  welcome_image: '',
  values: [
    { icon: 'heart', title: 'Love', description: 'We are called to love one another as Christ loved us. Love is the foundation of all we do.' },
    { icon: 'hand-helping', title: 'Compassion', description: "We show Christ's mercy by caring for those in need, both spiritually and physically." },
    { icon: 'users', title: 'Fellowship', description: 'We build authentic community where every person belongs and grows in faith together.' },
    { icon: 'shield-check', title: 'Integrity', description: 'We live transparently and honestly, honoring God in all our actions and decisions.' },
    { icon: 'gem', title: 'Respect', description: 'We treat every person with dignity, recognizing that all are made in the image of God.' },
    { icon: 'eye', title: 'Honesty', description: 'We commit to truthfulness in our words and actions, reflecting the character of Christ.' },
    { icon: 'handshake', title: 'Teamwork', description: 'We work together as the body of Christ, each contributing unique gifts for the common good.' },
  ],
};

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return inView;
}

function AnimatedSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function HomePage() {
  const [content, setContent] = useState(FALLBACK_HOME_CONTENT);
  const hasWelcomeImage = Boolean(content.welcome_image);

  useEffect(() => {
    fetchCached('/content/home').then((data) => {
      setContent({
        ...FALLBACK_HOME_CONTENT,
        ...data,
        values: data?.values?.length ? data.values : FALLBACK_HOME_CONTENT.values,
      });
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="mscm-public-shell">
      <section
        data-testid="hero-section"
        className="mscm-cinematic-hero relative min-h-[90vh] flex items-center justify-center overflow-hidden"
      >
        <img
          src={resolveImageUrl(content.hero_bg_image, { width: 1920, quality: 68 })}
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          loading="eager"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#071A42]/95 via-[#123D86]/90 to-[#1E4FA1]/60" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.10)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:80px_80px] opacity-35" />
        <div className="mscm-hero-aurora" />
        <div className="mscm-star-field" />
        <div className="mscm-star-field mscm-star-field-deep" />
        <div className="mscm-luminous-sweep" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#F7FBFF] to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.72fr)] gap-10 lg:gap-16 items-center">
            <div className="mscm-hero-copy max-w-4xl">
              <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white/90 backdrop-blur-md animate-fade-in-up">
                <Sparkles size={14} />
                <span className="min-w-0 truncate">Faith, fellowship, service</span>
              </div>
              <h1
                data-testid="hero-title"
                className="mscm-hero-title mt-6 font-bold text-white animate-fade-in-up animation-delay-100 lg:text-7xl"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {content.hero_title}
              </h1>
              <p
                data-testid="hero-subtitle"
                className="mt-6 text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed animate-fade-in-up animation-delay-200"
              >
                {content.hero_subtitle}
              </p>
              <div className="mscm-action-row mt-10 flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-400">
                <Link
                  to={content.hero_cta_link || '/contact'}
                  data-testid="hero-cta-button"
                  className="mscm-button-light px-8 py-4 text-base"
                >
                  {content.hero_cta_text || 'Join Us'}
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to="/activities"
                  data-testid="hero-activities-button"
                  className="mscm-button-ghost px-8 py-4 text-base"
                >
                  Annual Activities
                  <CalendarDays size={18} />
                </Link>
              </div>
            </div>

            <div className="mscm-orbit-stage hidden lg:flex items-center justify-end animate-fade-in-up animation-delay-500">
              <div className="mscm-brand-orb">
                <img src="/logo.png" alt="" aria-hidden="true" loading="lazy" decoding="async" />
              </div>
              <div className="mscm-floating-note mscm-floating-note-top p-4">
                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-white/60">Purpose</p>
                <p className="mt-2 text-lg font-bold text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Light across nations
                </p>
              </div>
              <div className="mscm-floating-note mscm-floating-note-bottom p-5">
                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-white/60">2027 Calendar</p>
                <div className="mt-4 space-y-3">
                  {[
                    ['FEB', 'Pastors and Wives Dinner'],
                    ['MAY', "Mothers and Daughters' Banquet"],
                    ['DEC', 'Konkou Chante Nwel'],
                  ].map(([month, title]) => (
                    <div key={title} className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#1E4FA1] text-[10px] font-black">
                        {month}
                      </span>
                      <span className="text-xs leading-snug text-white/80">{title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="welcome-section" className="mscm-section bg-[#F7FBFF]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className={`grid grid-cols-1 ${hasWelcomeImage ? 'lg:grid-cols-2 gap-16 items-center' : 'gap-10'}`}>
            <AnimatedSection className={hasWelcomeImage ? '' : 'max-w-4xl mx-auto'}>
              <div className="mscm-open-panel mscm-top-rule pt-6">
                <span className="mscm-eyebrow">Welcome</span>
                <h2
                  data-testid="welcome-title"
                  className="mscm-display mt-3 text-3xl md:text-4xl font-bold leading-snug"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {content.welcome_title}
                </h2>
                <p className="mt-6 text-[#526274] text-base leading-relaxed">
                  {content.welcome_text}
                </p>
                <div className="mt-8 flex gap-4">
                  <Link
                    to="/about"
                    data-testid="welcome-about-link"
                    className="mscm-button-primary px-6 py-3 text-sm sm:w-auto"
                  >
                    About Us <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
            {hasWelcomeImage && (
              <AnimatedSection delay={200}>
                <div className="relative">
                  <img
                    src={resolveImageUrl(content.welcome_image, { width: 1200, quality: 72 })}
                    alt="Community"
                    className="rounded-[6px] w-full h-[400px] object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </AnimatedSection>
            )}
          </div>
        </div>
      </section>

      <SectionWatermark
        id="mission-vision"
        className="mscm-section mscm-section-orbit mscm-diagonal-section"
        contentClassName="max-w-7xl mx-auto px-6 md:px-8"
        watermarkClassName="mscm-watermark-strong mscm-watermark-mission"
        opacity={0.24}
      >
        <AnimatedSection>
          <div className="mscm-section-intro mb-16">
            <span className="mscm-eyebrow">Our Purpose</span>
            <h2
              className="mscm-section-title mscm-display mt-3 font-bold md:text-5xl"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Mission & Vision
            </h2>
            <p className="mt-5 max-w-2xl text-[#526274] leading-relaxed">
              A clear calling, expressed through service, fellowship, and the light of Christ in every community we touch.
            </p>
          </div>
        </AnimatedSection>
        <div className="mscm-mission-layout grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          <AnimatedSection delay={100}>
            <div className="mscm-mission-statement h-full">
              <div className="mb-6 flex items-center justify-between gap-4">
                <div className="mscm-mission-symbol">
                  <Heart size={22} />
                  <span>Mission</span>
                </div>
                <span className="mscm-mission-number">01</span>
              </div>
              <h3 className="text-2xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Our Mission
              </h3>
              <p className="text-[#26364A] leading-relaxed">
                Shining the light of Christ among the nations, uniting people of all backgrounds into God's family, and serving with love as countless stars in His Kingdom.
              </p>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={200}>
            <div className="mscm-mission-statement h-full">
              <div className="mb-6 flex items-center justify-between gap-4">
                <div className="mscm-mission-symbol">
                  <Eye size={22} />
                  <span>Vision</span>
                </div>
                <span className="mscm-mission-number">02</span>
              </div>
              <h3 className="text-2xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Our Vision
              </h3>
              <p className="text-[#26364A] leading-relaxed">
                Being an inclusive Ministry that preaches the Gospel to everyone, every community through activities that show God's love for humanity.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </SectionWatermark>

      <section data-testid="values-section" className="mscm-section mscm-section-soft">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="mscm-eyebrow">What We Stand For</span>
              <h2
                className="mscm-display mt-3 text-3xl md:text-4xl font-bold"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Our Core Values
              </h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {(content.values || []).map((value, i) => {
              const IconComp = ICON_MAP[value.icon] || Heart;
              return (
                <AnimatedSection key={i} delay={i * 80}>
                  <div className="mscm-value-item p-5 h-full">
                    <div className="w-12 h-12 bg-[#EAF4FF]/70 rounded-[8px] flex items-center justify-center mb-4">
                      <IconComp size={22} className="text-[#1E4FA1]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#0F172A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {value.title}
                    </h3>
                    <p className="text-[#475569] text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="cta-section" className="mscm-section mscm-futuristic-band">
        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center relative z-10">
          <AnimatedSection>
            <h2
              className="mscm-section-title font-bold text-white md:text-4xl lg:text-5xl"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Be a Star in God's Kingdom
            </h2>
            <p className="mt-6 text-white/80 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Join our community and be part of something greater. Together, we can shine the light of Christ and make a difference in the world.
            </p>
            <div className="mscm-action-row mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="cta-get-involved-button"
                className="mscm-button-light px-8 py-4 text-base"
              >
                Get Involved
              </Link>
              <Link
                to="/activities"
                data-testid="cta-contact-button"
                className="mscm-button-ghost px-8 py-4 text-base"
              >
                View Activities
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
